import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanMatch,
  Route,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
  Router,
} from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { from, isObservable, Observable, of, timer } from 'rxjs';
import { map, switchMap, filter, take, catchError, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { StorageService } from './storage.service';
import { UserRolePermissionResponse } from '../models/user-roles-permissions';
import { UserService } from '../services/user-service';

type GuardResult = boolean | UrlTree;

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild, CanMatch {
  constructor(
    private readonly msalGuard: MsalGuard,
    private readonly authService: AuthService,
    private readonly storageService: StorageService,
    private readonly router: Router,
    private readonly userService: UserService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<GuardResult> {
    return this.enhanceGuardResult(this.msalGuard.canActivate(route, state), state.url);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<GuardResult> {
    return this.enhanceGuardResult(this.msalGuard.canActivateChild(childRoute, state), state.url);
  }

  canMatch(route: Route, segments: UrlSegment[]): Observable<GuardResult> {
    const targetUrl = '/' + segments.map((segment) => segment.path).join('/');
    return this.enhanceGuardResult(this.msalGuard.canMatch(), targetUrl);
  }

  private enhanceGuardResult(
    result: GuardResult | Observable<GuardResult> | Promise<GuardResult>,
    source?: string
  ): Observable<GuardResult> {
    return this.toObservable(result).pipe(
      switchMap((value) => {
        if (value !== true) {
          return of(value);
        }

        return this.authService.handleRedirectObservable().pipe(
          switchMap(() => this.waitForPermissionAndRoles()),
          map((hasRoles) => {
            if (!hasRoles) {
              return this.router.parseUrl('/unauthorized');
            }
            this.authService.handlePostLogin(source ?? 'GUARD_NAVIGATION');
            return true;
          })
        );
      })
    );
  }

  private waitForPermissionAndRoles(): Observable<boolean> {
    return this.ensurePermissionsLoaded().pipe(
      switchMap(() =>
        timer(0, 300).pipe(
          map(() => ({
            permissionChecked: this.isPermissionChecked(),
            storedUser: this.storageService.get('user'),
          })),
          filter(({ permissionChecked }) => permissionChecked === true),
          map(({ storedUser }) => this.hasRoles(storedUser)),
          take(1),
          catchError(() => of(false))
        )
      ),
      catchError(() => of(false))
    );
  }

  private isPermissionChecked(): boolean {
    const value = this.storageService.get('permissionChecked');
    return value === 'true';
  }

  private hasRoles(storedUser?: string | null): boolean {
    if (!storedUser) {
      return false;
    }
    try {
      const parsedUser = JSON.parse(storedUser) as UserRolePermissionResponse;
      if (!parsedUser?.isActive) {
        return false;
      }
      const parsedRoles = parsedUser.roles ?? [];
      if (Array.isArray(parsedRoles)) {
        return parsedRoles.length > 0;
      }
      if (parsedRoles && typeof parsedRoles === 'object') {
        return Object.keys(parsedRoles).length > 0;
      }
      return !!parsedRoles;
    } catch {
      return false;
    }
  }

  private ensurePermissionsLoaded(): Observable<void> {
    if (this.isPermissionChecked()) {
      return of(undefined);
    }

    return from(this.authService.getCurrentAccount()).pipe(
      switchMap((account) => {
        if (!account) {
          this.storageService.clear();
          this.storageService.set('permissionChecked', 'false');
          return of(undefined);
        }

        const email = account.username?.trim().toLowerCase();
        if (!email) {
          this.storageService.clear();
          this.storageService.set('permissionChecked', 'false');
          return of(undefined);
        }

        return this.userService.getUserPermissions(email).pipe(
          tap((response) => {
            if (response?.success && response.data) {
              this.storageService.set('user', response.data);
              this.storageService.set('permissionChecked', 'true');
              return;
            }

            this.storageService.remove('user');
            this.storageService.set('permissionChecked', 'true');
          }),
          map(() => undefined),
          catchError(() => {
            this.storageService.remove('user');
            this.storageService.set('permissionChecked', 'false');
            return of(undefined);
          })
        );
      })
    );
  }

  private toObservable(
    value: GuardResult | Observable<GuardResult> | Promise<GuardResult>
  ): Observable<GuardResult> {
    if (isObservable(value)) {
      return value;
    }

    if (value instanceof Promise) {
      return from(value);
    }

    return of(value);
  }
}
